import { useCallback, useMemo, useState } from 'react';

import { Center, Group, InputWrapper } from '@mantine/core';
import { useUncontrolled } from '@mantine/hooks';

import { ActionIcon, ButtonProps } from '../../../Buttons';
import { IconDelete, IconMinus, IconPlusTabler } from '../../../Icons';
import { Text } from '../../../Typography';

type QuantitySelectorProps = {
  size?: ButtonProps['size'];
  color?: 'gray' | 'cobalt';
  label?: React.ReactNode;
  value: number;
  minValue?: number;
  maxValue?: number;
  error?: string;
  onChange?: (quantity: number) => void;
  disabled?: boolean;
};

const useDecreaseQuantity = ({ value, minValue }: Partial<QuantitySelectorProps>) => {
  const decreaseActionDisabled = useMemo(() => {
    if (value === 0 || value === minValue) return true;
    return false;
  }, [value, minValue]);

  const decreaseActionIcon = useMemo(() => {
    if (value === 1) {
      return <IconDelete />;
    } else {
      return <IconMinus />;
    }
  }, [value]);

  return { decreaseActionIcon, decreaseActionDisabled };
};

const QuantitySelector = ({
  size,
  color,
  label,
  value: _value,
  minValue,
  maxValue,
  error,
  onChange: _onChange,
  disabled,
}: QuantitySelectorProps) => {
  const [value, onChange] = useUncontrolled({
    value: _value,
    onChange: _onChange,
  });
  const [_error, setError] = useState<string | undefined>(error);

  const actionIconVariant = color === 'gray' ? 'gray' : 'filled';
  const actionIconSize = size === 'lg' ? 'sm' : 'xs';

  const { decreaseActionIcon, decreaseActionDisabled } = useDecreaseQuantity({
    value: value,
    minValue,
  });

  const handleQuantityIncrease = useCallback<React.MouseEventHandler<HTMLButtonElement>>(
    (e) => {
      e.stopPropagation();

      const newQuantity = value + 1;
      onChange?.(newQuantity);

      if (_error) {
        setError(undefined);
      }
    },
    [value, onChange, _error]
  );

  const handleQuantityDecrease = useCallback<React.MouseEventHandler<HTMLButtonElement>>(
    (e) => {
      e.stopPropagation();

      const newQuantity = value - 1;
      onChange?.(newQuantity);

      if (_error) {
        setError(undefined);
      }
    },
    [value, onChange, _error]
  );

  return (
    <InputWrapper error={_error} errorProps={{ mt: '4px' }}>
      <Group gap={size === 'lg' ? 'md' : 'xs'} justify="space-between">
        {!!label &&
          (typeof label === 'string' ? (
            <Text fz="sm" c="gray.4">
              {label}
            </Text>
          ) : (
            label
          ))}
        <Group gap="xs" display="inline-flex" wrap="nowrap">
          <ActionIcon
            variant={actionIconVariant}
            size={actionIconSize}
            onClick={handleQuantityDecrease}
            disabled={decreaseActionDisabled || disabled}
            aria-label="decrease"
          >
            {decreaseActionIcon}
          </ActionIcon>
          <Center w={32}>
            <Text fz="sm" fw={600}>
              {value}
            </Text>
          </Center>
          <ActionIcon
            variant={actionIconVariant}
            size={actionIconSize}
            onClick={handleQuantityIncrease}
            disabled={(maxValue ? value === maxValue : undefined) || disabled}
            aria-label="increase"
          >
            <IconPlusTabler brand="tabler" />
          </ActionIcon>
        </Group>
      </Group>
    </InputWrapper>
  );
};

export { QuantitySelector };
export type { QuantitySelectorProps };
